/**
 * Build the `/plot` request from the current atoms (blob `buildPlotRequest`).
 * Pure reads - no DOM, no network. Every data source takes the same request;
 * a non-default one is named in `source`.
 *
 * Configuration and data shaping can also be passed explicitly rather than read
 * from the shared atoms (`opts.state` / `opts.shaping`). That is how dashboard
 * tiles build many differently-configured requests at once, with no global to
 * swap and restore.
 *
 * `plotRequestSignature` fingerprints a built body so the Plot button can tell
 * whether pressing it again would fetch exactly what is already drawn.
 */

import {
  selectedShip,
  startDate,
  endDate,
  activeDataSource,
} from '../../state/store.ts';
import { plotState, timeColumn, type PlotState } from '../../state/plot-state.ts';
import { sourceField } from '../../state/data-sources.ts';
import { buildShaping, type ShapingFragment } from '../../state/request.ts';
import { slicerColumns } from './slicers.ts';
import { groupsForRequest, measuresForRequest, readingsOrder } from './table-config.ts';
import { dialsForRequest } from './gauge-config.ts';
import { valuesForRequest } from './sankey-config.ts';
import { PLOT_TARGET_POINTS, SCATTER_TARGET_POINTS, MAP_TARGET_POINTS } from './plot-constants.ts';
import type {
  PlotRequest,
  HistogramPlotRequest,
  HeatmapPlotRequest,
  CorrelationPlotRequest,
  PiePlotRequest,
  BoxPlotRequest,
  MapPlotRequest,
  XYPlotRequest,
  FitSpec,
} from '../../api/types.ts';

/**
 * The data-shaping fragment spread onto every historical body: filters,
 * outliers and derived signals. Named so a caller that is not Explore can pass
 * its own instead of the shared atoms' (dashboard tiles carry theirs in the tile
 * spec).
 */
export type RequestShaping = ShapingFragment;

/** Optional overrides - drag-zoom windows, another ship, explicit configuration. */
export interface PlotRequestOpts {
  /** Build for this ship instead of the anchor - the multi-ship fan-out. */
  ship?: string;
  start?: string;
  end?: string;
  x_min?: number;
  x_max?: number;
  /**
   * Chart configuration to build from. Defaults to the global `plotState` -
   * i.e. what Explore is showing. A dashboard tile resolves its own from its
   * query string and passes it here, which is what lets many differently
   * configured charts be in flight at once against one global atom.
   */
  state?: PlotState;
  /** Data shaping to attach. Defaults to the shared filter/outlier/derived atoms. */
  shaping?: RequestShaping;
  /**
   * Data source to read. Defaults to Explore's. A dashboard tile passes its
   * own, so what Explore happens to be showing never changes a tile.
   */
  source?: string;
}

export interface BuiltPlotRequest {
  body: PlotRequest;
}

export function buildPlotRequest(opts?: PlotRequestOpts): BuiltPlotRequest {
  const ds = opts?.source ?? activeDataSource.value;
  const built = buildForSource(opts);
  // The default source is never written: bodies, and every fingerprint of
  // them, are what they were before sources existed.
  return { body: { ...built.body, ...sourceField(ds) } };
}

function buildForSource(opts?: PlotRequestOpts): BuiltPlotRequest {
  const s = opts?.state ?? plotState.value;
  const t = s.plot_type;
  // Line, Scatter and Map slice locally: their slicer rows are not sent as
  // conditions, the columns are fetched per point instead (`slice_columns`).
  const localSlicing = !opts?.shaping && (t === 'line' || t === 'scatter' || t === 'gauge' || t === 'map');
  const shaping = opts?.shaping ?? buildShaping(undefined, { omitSlicers: localSlicing });
  const shipId = opts?.ship ?? selectedShip.value ?? '';
  const start = opts?.start ?? startDate.value;
  const end = opts?.end ?? endDate.value;
  const maxPoints = t === 'scatter' ? SCATTER_TARGET_POINTS : PLOT_TARGET_POINTS;
  const ab = s.axis_bounds;
  if (t === 'number') {
    return { body: {
      plot_type: 'number', ship_id: shipId, start_date: start, end_date: end,
      number_mode: s.number_mode,
      ...(s.number_mode === 'formula'
        ? { number_expression: s.number_expression }
        : { number_column: s.number_column, number_agg: s.number_agg }),
      ...shaping,
    } };
  }

  if (t === 'gauge') {
    return { body: {
      plot_type: 'gauge', ship_id: shipId, start_date: start, end_date: end,
      gauge_dials: dialsForRequest(s.gauge_dials),
      ...shaping,
      // Slicers move the needles in the browser, from readings sent with the
      // dials, the way Line and Scatter slice their points.
      ...(localSlicing && slicerColumns().length ? { slice_columns: slicerColumns() } : {}),
    } };
  }

  if (t === 'sankey') {
    // Only what the Signal and Calculation nodes read; the diagram is drawn
    // from editor state, so editing links or labels never needs a query.
    return { body: {
      plot_type: 'sankey', ship_id: shipId, start_date: start, end_date: end,
      sankey_values: valuesForRequest(s.sankey_nodes),
      ...shaping,
    } };
  }

  if (t === 'table') {
    return { body: {
      plot_type: 'table', ship_id: shipId, start_date: start, end_date: end,
      table_mode: s.table_mode,
      // Only the active mode's inputs travel, as for the pie and box.
      ...(s.table_mode === 'readings'
        ? {
            table_columns: [...s.table_columns],
            table_offset: s.table_page * s.table_page_size,
            table_limit: s.table_page_size,
            ...readingsOrder(s.table_sort, s.table_columns),
          }
        : { table_groups: groupsForRequest(s.table_groups), table_measures: measuresForRequest(s.table_measures) }),
      ...shaping,
    } };
  }

  /* ── Histogram ────────────────────────────────────────── */
  if (t === 'histogram') {
    const body: HistogramPlotRequest = {
      plot_type: 'histogram',
      ship_id: shipId,
      start_date: start,
      end_date: end,
      hist_columns: s.hist_columns,
      hist_bins: s.hist_bins,
    };
    Object.assign(body, shaping);
    return { body };
  }

  /* ── Heatmap (2D binning, aggregated server-side) ─────── */
  if (t === 'heatmap') {
    const body: HeatmapPlotRequest = {
      plot_type: 'heatmap',
      ship_id: shipId,
      start_date: start,
      end_date: end,
      x_column: s.x_column,
      y_column: s.y_column,
    };
    // All three are optional: with none of them the server picks the cell size
    // from the data's own spread.
    if (s.heat_bins) body.bins = s.heat_bins;
    if (s.heat_x_step) body.x_step = s.heat_x_step;
    if (s.heat_y_step) body.y_step = s.heat_y_step;
    // Both or neither: an aggregate with no column picked yet is still a
    // density heatmap, which is what the control says it will be.
    if (s.z_column && s.z_agg) {
      body.z_column = s.z_column;
      body.z_agg = s.z_agg;
    }
    // Axis bounds do double duty here: they pin the grid, which is the only way
    // two ships in grid view get identical cell edges.
    if (ab.x_min != null) body.x_min = ab.x_min;
    if (ab.x_max != null) body.x_max = ab.x_max;
    if (ab.y_min != null) body.y_min = ab.y_min;
    if (ab.y_max != null) body.y_max = ab.y_max;
    Object.assign(body, shaping);
    return { body };
  }

  /* ── Correlation matrix (every pair, aggregated server-side) ── */
  if (t === 'correlation') {
    const body: CorrelationPlotRequest = {
      plot_type: 'correlation',
      ship_id: shipId,
      start_date: start,
      end_date: end,
      corr_columns: s.corr_columns,
      method: s.corr_method,
      order: s.corr_order,
    };
    Object.assign(body, shaping);
    return { body };
  }

  /* ── Pie (share of a whole, aggregated server-side) ──── */
  if (t === 'pie') {
    // Only the active mode's inputs travel: the other mode's are kept in state
    // for flipping back, and would otherwise make two requests for the same
    // chart look different to the plot cache and the catalog.
    const body: PiePlotRequest =
      s.pie_mode === 'bands'
        ? {
            plot_type: 'pie',
            ship_id: shipId,
            start_date: start,
            end_date: end,
            pie_mode: 'bands',
            pie_band_column: s.pie_band_column,
            ...(s.pie_band_edges?.length
              ? { pie_band_edges: s.pie_band_edges }
              : { pie_bands: s.pie_bands }),
          }
        : {
            plot_type: 'pie',
            ship_id: shipId,
            start_date: start,
            end_date: end,
            pie_mode: 'signals',
            pie_columns: s.pie_columns,
            pie_agg: s.pie_agg,
          };
    Object.assign(body, shaping);
    return { body };
  }

  /* ── Box plot (distributions, summarised server-side) ── */
  if (t === 'box') {
    // Only the active mode's inputs travel, for the same reason as the pie's.
    const body: BoxPlotRequest =
      s.box_mode === 'groups'
        ? {
            plot_type: 'box',
            ship_id: shipId,
            start_date: start,
            end_date: end,
            box_mode: 'groups',
            box_column: s.box_column,
            box_group_column: s.box_group_column,
          }
        : {
            plot_type: 'box',
            ship_id: shipId,
            start_date: start,
            end_date: end,
            box_mode: 'signals',
            box_columns: s.box_columns,
          };
    Object.assign(body, shaping);
    return { body };
  }

  /* ── Map (lat/lon track) ──────────────────────────────── */
  if (t === 'map') {
    const body: MapPlotRequest = {
      plot_type: 'map',
      ship_id: shipId,
      start_date: start,
      end_date: end,
      latitude_column: s.lat_column,
      longitude_column: s.lon_column,
      time_column: timeColumn(),
      max_points: MAP_TARGET_POINTS,
    };
    if (s.color_by) body.color_by = s.color_by;
    Object.assign(body, shaping);
    const cols = slicerColumns();
    if (localSlicing && cols.length) body.slice_columns = cols;
    return { body };
  }

  /* ── Line / scatter ───────────────────────────────────── */
  // One structural shape for both; the literal `plot_type` discriminates on return.
  const body: XYPlotRequest & { plot_type: 'line' | 'scatter' } = {
    plot_type: t,
    ship_id: shipId,
    start_date: start,
    end_date: end,
    x_column: s.x_column,
    y_left: s.y_left,
    y_right: s.y_right,
    max_points: maxPoints,
  };
  Object.assign(body, shaping);
  if (localSlicing) {
    const cols = slicerColumns();
    if (cols.length) body.slice_columns = cols;
  }

  if (t === 'line') body.show_legs = s.show_legs;
  // Both line and scatter may be coloured by a column: the scatter colours its
  // points, the line its segments. The backend extracts the column the same way
  // for either.
  if (s.color_by) body.color_by = s.color_by;

  // Fits apply to selected series only.
  if (s.fits.length) {
    const sel = new Set([...s.y_left, ...s.y_right]);
    const fits: FitSpec[] = s.fits
      .filter((f) => sel.has(f.series))
      .map((f) => ({ series: f.series, kind: f.kind, params: f.params || {} }));
    if (fits.length) body.fits = fits;
  }

  if (ab.y_min != null) body.y_min = ab.y_min;
  if (ab.y_max != null) body.y_max = ab.y_max;
  if (ab.yr_min != null) body.y_right_min = ab.yr_min;
  if (ab.yr_max != null) body.y_right_max = ab.yr_max;
  if (opts?.x_min != null) body.x_min = opts.x_min;
  if (opts?.x_max != null) body.x_max = opts.x_max;

  return { body: body as PlotRequest };
}

/**
 * Stable fingerprint of a built request body: two bodies that would produce the
 * same chart share a signature. Object keys are sorted so a difference in build
 * order never reads as a settings change; array order is significant (series
 * order drives colours and axis assignment), so arrays are left alone.
 *
 * Presentation-only settings (line width, point size, alpha, custom colours)
 * never reach the request body - they redraw from cached data - so they
 * correctly leave the signature untouched.
 */
export function plotRequestSignature(
  body: PlotRequest,
  ships?: string[],
): string {
  // The ship set is part of "would this draw something different?" - without it
  // the Plot button would stay disabled after adding a comparison ship.
  // A gauge's values are matched to its dials by what each reads, so the order
  // of the dials - and a duplicate - never needs another query.
  // A Sankey's are matched to its nodes the same way.
  const normal = 'gauge_dials' in body
    ? { ...body, gauge_dials: [...new Set(body.gauge_dials.map((d) => JSON.stringify(canonicalize(d))))].sort() }
    : 'sankey_values' in body
      ? { ...body, sankey_values: [...new Set(body.sankey_values.map((d) => JSON.stringify(canonicalize(d))))].sort() }
      : body;
  return JSON.stringify({ ships: ships ?? null, body: canonicalize(normal) });
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value !== null && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(obj).sort()) {
      if (obj[key] === undefined) continue;
      out[key] = canonicalize(obj[key]);
    }
    return out;
  }
  return value;
}
